import {
    Component,
    OnInit,
    ViewChild,
    AfterViewInit,
    OnDestroy,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, firstValueFrom, merge } from 'rxjs';
import * as ExcelJS from 'exceljs';

// Angular Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import {
    MatTableModule,
    MatTableDataSource,
    MatTable,
} from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DocumentosOrdenServicioDialogComponent } from './documentos-orden-servicio-dialog/documentos-orden-servicio-dialog.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Interfaces locales del frontend
export interface IOsAgenciaItem {
    id: number;
    shippingInstructionId: number | null;
    transportOrderId: number | null;
    shippingLiquidationId: number | null;
    customerId: number;
    customerName: string;
    serviceName: string;
    vesselName: string;
    typeOfInvoicingName: string;
    bookingNumber: string | null;
    containerNumber: string | null;
    portOfDischargeName: string | null;
    typeOfShipmentName: string;
    typeOfShipmentId: number;
    totalSellingPriceAmount: number;
    totalManagementFeeAmount: number;
    etdDate: Date | null;
    creationDate: Date;
    createdBy: string;
    customerReference: string | null;
    remarks: string | null;
    providerName?: string | null;
    invoiceNumber?: string | null;
}

export interface IOsAgenciaFilters {
    customerId?: number;
    serviceId?: number;
    vesselId?: number;
    typeOfInvoicingId?: number;
    osNumber?: number;
    shippingInstructionId?: number;
    transportOrderId?: number;
    shippingLiquidationId?: number;
    bookingNumber?: string;
    containerNumber?: string;
    dusNumber?: string;
    temporadaId?: number;
    fromDate?: string;
    toDate?: string;
}

export interface IOsAgenciaQuery extends IOsAgenciaFilters {
    tabType: OsAgenciaTabType;
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: 'ASC' | 'DESC';
}

export interface IOsAgenciaTotals {
    totalVentaNeto: number;
    totalTraspasoBruto: number;
    totalGeneral: number;
}

export interface IOsAgenciaPaginatedResponse {
    data: IOsAgenciaItem[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    tabSummary: string;
    appliedFilters: IOsAgenciaFilters;
    totals: IOsAgenciaTotals;
}

// Enum usado indirectamente a través de OsAgenciaTabType.ORDER_SERVICES, etc.
// eslint-disable-next-line no-unused-vars
export enum OsAgenciaTabType {
    ORDER_SERVICES = 'ORDER_SERVICES',
    TRANSPORT = 'TRANSPORT',
    ADDITIONAL_COST = 'ADDITIONAL_COST',
}

// Servicios
import { OsAgenciaService } from '../../../services';
import { CommonsService } from '../../../services/commons.service';
import { ClientService, Client } from '../../../services/client.service';
import { UserService } from '../../../services/user.service';
import {
    IServiceFilter,
    IVesselFilter,
    IInvoicingTypeFilter,
    ISeasonFilter,
    IOsAgenciaDetail,
} from '../../../services/os-agencia.service';
import { LoggerService } from '../../../core/services/logger.service';
import { AuthService } from '../../../services/auth.service';
import {
    formatDateForApi,
    getDefaultDateRange,
} from '../../../core/utils/date.utils';

/**
 * Interfaz local para gestión de datos en el componente
 */
interface OrdenServicioAgenciaLocal extends IOsAgenciaItem {
    // Campos calculados para la tabla
    numeroLiquidacion?: string;
    totalNeto?: number;
    formattedEtd?: string;
    formattedCreationDate?: string;
}

/**
 * Interfaz para totales por tab
 */
interface OrdenServicioAgenciaTotales {
    count: number;
    totalVentaNeto: number;
    totalTraspasoBruto: number;
    totalGeneral: number;
}

/** Limite maximo de opciones visibles en dropdowns autocomplete */
const AUTOCOMPLETE_MAX_OPTIONS = 200;

@Component({
    selector: 'app-ordenes-servicio-agencia',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatMenuModule,
        MatChipsModule,
        MatTooltipModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        ReactiveFormsModule,
        MatSnackBarModule,
        MatTabsModule,
        MatBadgeModule,
        MatProgressSpinnerModule,
        MatDialogModule,
    ],
    templateUrl: './ordenes-servicio-agencia.component.html',
    styleUrls: ['./ordenes-servicio-agencia.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class OrdenesServicioAgenciaComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private destroy$ = new Subject<void>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatTable) table!: MatTable<OrdenServicioAgenciaLocal>;

    // Configuración de tabs
    tabs = [
        {
            type: OsAgenciaTabType.ORDER_SERVICES,
            label: 'Órdenes de servicio',
            icon: 'assignment',
            description:
                'O/S con instrucción de embarque o sin orden de transporte',
        },
        {
            type: OsAgenciaTabType.TRANSPORT,
            label: 'Transporte',
            icon: 'local_shipping',
            description:
                'O/S sin instrucción de embarque o con orden de transporte',
        },
        {
            type: OsAgenciaTabType.ADDITIONAL_COST,
            label: 'Extra costos',
            icon: 'add_circle',
            description:
                'O/S sin instrucción de embarque ni orden de transporte',
        },
    ];

    selectedTabIndex = 0;
    currentTabType = OsAgenciaTabType.ORDER_SERVICES;

    // Columnas de la tabla según wireframe
    displayedColumns: string[] = [
        'nroOs',
        'nroLiquidacion',
        'cliente',
        'servicio',
        'nave',
        'tipoFacturacion',
        'inicio',
        'fin',
        'refCliente',
        'contenedor',
        'booking',
        'nroDus',
        'ventaNeta',
        'traspasoBruto',
        'acciones',
    ];

    // Datos de la tabla
    dataSource = new MatTableDataSource<OrdenServicioAgenciaLocal>([]);

    // Rol del usuario
    isClientUser = false;

    // Estados de carga
    isLoading = false;
    hasError = false;
    errorMessage = '';
    isGeneratingExcel = false;

    // Datos de paginación y totales
    totalRegistros = 0;
    totalPaginas = 0;
    paginaActual = 1;
    elementosPorPagina = 10;

    // Totales por tab
    totales: OrdenServicioAgenciaTotales = {
        count: 0,
        totalVentaNeto: 0,
        totalTraspasoBruto: 0,
        totalGeneral: 0,
    };

    // Contadores por tab para badges
    // eslint-disable-next-line no-unused-vars
    tabCounts: { [key in OsAgenciaTabType]: number } = {
        [OsAgenciaTabType.ORDER_SERVICES]: 0,
        [OsAgenciaTabType.TRANSPORT]: 0,
        [OsAgenciaTabType.ADDITIONAL_COST]: 0,
    };

    // Filtros según wireframe
    filtroCliente = new FormControl<string | number>('');
    filtroServicio = new FormControl<string | number>('');
    filtroNave = new FormControl<string | number>('');
    filtroTipoFacturacion = new FormControl<number | null>(null);
    filtroTemporada = new FormControl<number | null>(null);
    filtroInicio = new FormControl<Date | null>(null);
    filtroFin = new FormControl<Date | null>(null);
    filtroRefCliente = new FormControl('');
    filtroContenedor = new FormControl('');
    filtroDus = new FormControl('');
    filtroNroLiquidacion = new FormControl('');
    filtroBooking = new FormControl('');

    // Opciones para selectores (se cargarán dinámicamente)
    clientesOptions: Client[] = [];
    filteredClientesOptions: Client[] = [];
    serviciosOptions: IServiceFilter[] = [];
    filteredServiciosOptions: IServiceFilter[] = [];
    navesOptions: IVesselFilter[] = [];
    filteredNavesOptions: IVesselFilter[] = [];
    tiposFacturacionOptions: IInvoicingTypeFilter[] = [];
    temporadasOptions: ISeasonFilter[] = [];

    constructor(
        // eslint-disable-next-line no-unused-vars
        private osAgenciaService: OsAgenciaService,
        // eslint-disable-next-line no-unused-vars
        private commonsService: CommonsService,
        // eslint-disable-next-line no-unused-vars
        private clientService: ClientService,
        // eslint-disable-next-line no-unused-vars
        private userService: UserService,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private dialog: MatDialog,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private authService: AuthService
    ) {}

    ngOnInit(): void {
        this.logger.info(
            '🔄 OrdenesServicioAgenciaComponent: Inicializando componente'
        );
        this.setupDateLockBehavior();
        this.setupFilters();
        this.setupClientFilter();
        this.restoreStateFromQueryParams();

        // Fechas por defecto si no hay state previo
        if (!this.filtroInicio.value && !this.filtroFin.value) {
            const { from, to } = getDefaultDateRange();
            this.filtroInicio.setValue(from);
            this.filtroFin.setValue(to);
        }

        this.loadInitialData();
    }

    /**
     * Restaura el estado de filtros y paginación desde los query params de la URL
     */
    private restoreStateFromQueryParams(): void {
        const queryParams = this.route.snapshot.queryParams;

        // Restaurar paginación
        if (queryParams['page']) {
            const page = parseInt(queryParams['page'], 10);
            if (!isNaN(page) && page > 0) {
                this.paginaActual = page;
            }
        }

        if (queryParams['limit']) {
            const limit = parseInt(queryParams['limit'], 10);
            if (!isNaN(limit) && limit > 0) {
                this.elementosPorPagina = limit;
            }
        }

        // Restaurar filtros
        if (queryParams['customerId']) {
            const customerId = parseInt(queryParams['customerId'], 10);
            if (!isNaN(customerId)) {
                this.filtroCliente.setValue(customerId);
            }
        }

        if (queryParams['serviceId']) {
            const serviceId = parseInt(queryParams['serviceId'], 10);
            if (!isNaN(serviceId)) {
                this.filtroServicio.setValue(serviceId);
            }
        }

        if (queryParams['vesselId']) {
            const vesselId = parseInt(queryParams['vesselId'], 10);
            if (!isNaN(vesselId)) {
                this.filtroNave.setValue(vesselId);
            }
        }

        if (queryParams['typeOfInvoicingId']) {
            const typeOfInvoicingId = parseInt(
                queryParams['typeOfInvoicingId'],
                10
            );
            if (!isNaN(typeOfInvoicingId)) {
                this.filtroTipoFacturacion.setValue(typeOfInvoicingId);
            }
        }

        if (queryParams['containerNumber']) {
            this.filtroContenedor.setValue(queryParams['containerNumber']);
        }

        if (queryParams['bookingNumber']) {
            this.filtroBooking.setValue(queryParams['bookingNumber']);
        }

        if (queryParams['shippingLiquidationId']) {
            const liquidacionId = parseInt(
                queryParams['shippingLiquidationId'],
                10
            );
            if (!isNaN(liquidacionId)) {
                this.filtroNroLiquidacion.setValue(liquidacionId.toString());
            }
        }

        if (queryParams['refCliente']) {
            this.filtroRefCliente.setValue(queryParams['refCliente']);
        }

        if (queryParams['fromDate']) {
            const fromDate = new Date(queryParams['fromDate']);
            if (!isNaN(fromDate.getTime())) {
                this.filtroInicio.setValue(fromDate);
            }
        }

        if (queryParams['toDate']) {
            const toDate = new Date(queryParams['toDate']);
            if (!isNaN(toDate.getTime())) {
                this.filtroFin.setValue(toDate);
            }
        }

        this.logger.info(
            `📋 Estado restaurado desde query params: page=${this.paginaActual}, limit=${this.elementosPorPagina}`,
            'OrdenesServicioAgenciaComponent'
        );
    }

    /**
     * Configura el filtro de cliente según el tipo de usuario
     */
    private setupClientFilter(): void {
        const currentUser = this.authService.getCurrentUser();
        this.isClientUser =
            !!currentUser?.roles.includes('client') && !!currentUser?.clientId;

        if (this.isClientUser) {
            // Si es usuario cliente, establecer y deshabilitar el filtro
            this.filtroCliente.setValue(currentUser!.clientId!);
            this.filtroCliente.disable();
            // Ocultar columna cliente (ya se sabe quién es)
            this.displayedColumns = this.displayedColumns.filter(
                (col) => col !== 'cliente'
            );
            this.logger.info(
                '👤 Usuario cliente detectado, filtro y columna cliente ocultos'
            );
        } else {
            this.logger.info(
                '👤 Usuario no es cliente, filtro de cliente habilitado'
            );
        }
    }

    ngAfterViewInit(): void {
        // Configurar paginador y ordenamiento
        if (this.paginator && this.sort) {
            // Asignar el sort al dataSource
            this.dataSource.sort = this.sort;

            // Restaurar paginación desde query params si existe
            const queryParams = this.route.snapshot.queryParams;
            if (queryParams['page'] || queryParams['limit']) {
                setTimeout(() => {
                    if (queryParams['page']) {
                        const page = parseInt(queryParams['page'], 10);
                        if (!isNaN(page) && page > 0) {
                            this.paginator.pageIndex = page - 1;
                        }
                    }
                    if (queryParams['limit']) {
                        const limit = parseInt(queryParams['limit'], 10);
                        if (!isNaN(limit) && limit > 0) {
                            this.paginator.pageSize = limit;
                        }
                    }
                }, 100);
            }

            // Suscribirse a cambios de ordenamiento
            this.sort.sortChange.subscribe(() => {
                this.paginator.pageIndex = 0; // Reset a primera página al ordenar
                this.paginaActual = 1;
                this.buscarOrdenes();
            });
        }
    }

    /**
     * Maneja el evento de cambio de página del paginador
     */
    onPageChange(event: any): void {
        this.logger.info(
            '📄 Cambio de página: ' +
                JSON.stringify({
                    pageIndex: event.pageIndex,
                    pageSize: event.pageSize,
                    previousPageIndex: event.previousPageIndex,
                    length: event.length,
                })
        );

        // Actualizar variables de paginación
        this.paginaActual = event.pageIndex + 1;
        this.elementosPorPagina = event.pageSize;

        // Buscar órdenes con la nueva paginación
        this.buscarOrdenes();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get datesLockedBySearch(): boolean {
        return this.filtroInicio.disabled;
    }

    private setupDateLockBehavior(): void {
        merge(
            this.filtroContenedor.valueChanges,
            this.filtroDus.valueChanges,
            this.filtroNroLiquidacion.valueChanges,
            this.filtroBooking.valueChanges,
        ).subscribe(() => {
            const hasValue =
                !!this.filtroContenedor.value?.trim() ||
                !!this.filtroDus.value?.trim() ||
                !!this.filtroNroLiquidacion.value?.trim() ||
                !!this.filtroBooking.value?.trim();

            if (hasValue) {
                this.filtroInicio.setValue(null, { emitEvent: false });
                this.filtroFin.setValue(null, { emitEvent: false });
                this.filtroInicio.disable({ emitEvent: false });
                this.filtroFin.disable({ emitEvent: false });
            } else {
                this.filtroInicio.enable({ emitEvent: false });
                this.filtroFin.enable({ emitEvent: false });
            }
        });
    }

    /**
     * Configura los filtros para autocompletar (sin búsqueda automática)
     */
    private setupFilters(): void {
        // Suscribirse a los cambios solo para actualizar las opciones de autocompletar
        this.filtroCliente.valueChanges.subscribe((value) => {
            this.updateFilteredClientes(value);
        });
        this.filtroServicio.valueChanges.subscribe((value) => {
            this.updateFilteredServicios(value);
        });
        this.filtroNave.valueChanges.subscribe((value) => {
            this.updateFilteredNaves(value);
        });
    }

    /**
     * Carga datos iniciales y contadores para tabs.
     * Las temporadas se cargan primero para que el filtro default (última temporada)
     * esté seteado antes de lanzar la primera búsqueda.
     */
    private async loadInitialData(): Promise<void> {
        try {
            // 1. Cargar temporadas primero y setear la default
            await this.loadSeasons();

            // 2. El resto en paralelo — buscarOrdenes ya tiene filtroTemporada seteado.
            // loadTabCounts() no va aquí: buscarOrdenes() ya lo llama internamente.
            await Promise.all([
                this.loadServices(),
                this.loadVessels(),
                this.loadInvoicingTypes(),
                this.loadClients(),
                this.buscarOrdenes(),
            ]);
        } catch (error) {
            this.handleError('Error al cargar datos iniciales', error);
        }
    }

    /**
     * Carga la lista de clientes activos desde el endpoint /api/v1/clients/active
     */
    private async loadClients(): Promise<void> {
        try {
            const response = await this.userService
                .getActiveClients()
                .toPromise();
            if (response?.success) {
                this.clientesOptions = response.data || [];
                this.logger.info(
                    `✅ Cargados ${this.clientesOptions.length} clientes activos`
                );
                this.updateFilteredClientes(this.filtroCliente.value);

                // Forzar refresco de display cuando el valor es numérico (usuario cliente o valor restaurado)
                const currentValue = this.filtroCliente.value;
                if (
                    currentValue !== null &&
                    currentValue !== undefined &&
                    currentValue !== ''
                ) {
                    // Forzar actualización del display para que muestre el nombre correcto
                    this.filtroCliente.setValue(currentValue, {
                        emitEvent: false,
                    });
                }
            }
        } catch (error) {
            this.handleError('Error al cargar clientes', error);
            this.clientesOptions = [];
            this.filteredClientesOptions = [];
        }
    }

    /**
     * Carga la lista de tipos de facturación desde el endpoint de filtros
     */
    private async loadInvoicingTypes(): Promise<void> {
        try {
            this.tiposFacturacionOptions =
                (await this.osAgenciaService
                    .getInvoicingTypesFilter()
                    .toPromise()) || [];
            this.logger.info(
                `✅ Cargados ${this.tiposFacturacionOptions.length} tipos de facturación`
            );
        } catch (error) {
            this.handleError('Error al cargar tipos de facturación', error);
            this.tiposFacturacionOptions = [];
        }
    }

    /**
     * Carga la lista de temporadas desde el endpoint de filtros
     * La primera temporada (más reciente) se selecciona por defecto
     */
    private async loadSeasons(): Promise<void> {
        try {
            this.temporadasOptions =
                (await this.osAgenciaService.getSeasonsFilter().toPromise()) ||
                [];
            this.logger.info(
                `✅ Cargadas ${this.temporadasOptions.length} temporadas`
            );

            // Seleccionar la primera temporada (más reciente) por defecto si no hay una seleccionada
            if (
                this.temporadasOptions.length > 0 &&
                !this.filtroTemporada.value
            ) {
                this.filtroTemporada.setValue(
                    this.temporadasOptions[0].seasonId
                );
                this.logger.info(
                    `📅 Temporada por defecto: ${this.temporadasOptions[0].seasonName}`
                );
            }
        } catch (error) {
            this.handleError('Error al cargar temporadas', error);
            this.temporadasOptions = [];
        }
    }

    /**
     * Carga la lista de naves desde el endpoint de commons
     */
    private async loadVessels(): Promise<void> {
        try {
            this.navesOptions =
                (await this.osAgenciaService.getVesselsFilter().toPromise()) ||
                [];
            this.logger.info(`✅ Cargadas ${this.navesOptions.length} naves`);
            this.updateFilteredNaves(this.filtroNave.value);

            // Forzar actualización del display si hay un valor restaurado
            const currentValue = this.filtroNave.value;
            if (
                currentValue !== null &&
                currentValue !== undefined &&
                currentValue !== ''
            ) {
                this.filtroNave.setValue(currentValue, { emitEvent: false });
            }
        } catch (error) {
            this.handleError('Error al cargar naves', error);
            this.navesOptions = [];
            this.filteredNavesOptions = [];
        }
    }

    /**
     * Carga la lista de servicios desde el endpoint de commons
     */
    private async loadServices(): Promise<void> {
        try {
            this.serviciosOptions =
                (await this.osAgenciaService.getServicesFilter().toPromise()) ||
                [];
            this.logger.info(
                `✅ Cargados ${this.serviciosOptions.length} servicios`
            );
            this.updateFilteredServicios(this.filtroServicio.value);

            // Forzar actualización del display si hay un valor restaurado
            const currentValue = this.filtroServicio.value;
            if (
                currentValue !== null &&
                currentValue !== undefined &&
                currentValue !== ''
            ) {
                this.filtroServicio.setValue(currentValue, {
                    emitEvent: false,
                });
            }
        } catch (error) {
            this.handleError('Error al cargar servicios', error);
            this.serviciosOptions = [];
            this.filteredServiciosOptions = [];
        }
    }

    /**
     * Carga contadores para cada tab
     */
    private async loadTabCounts(): Promise<void> {
        for (const tab of this.tabs) {
            try {
                const filters = this.buildFilters();
                const query: IOsAgenciaQuery = {
                    ...filters,
                    tabType: tab.type,
                    page: 1,
                    limit: 1, // Solo necesitamos el total
                };

                const response = await this.osAgenciaService
                    .getOrdenes(query)
                    .toPromise();
                if (response) {
                    this.tabCounts[tab.type] = response.total;
                }
            } catch (error) {
                this.logger.error(
                    `Error cargando contador para tab ${tab.type}:`,
                    error as Error
                );
                this.tabCounts[tab.type] = 0;
            }
        }
    }

    /**
     * Maneja el cambio de tab
     */
    onTabChange(event: any): void {
        this.logger.info(
            `📋 Iniciando cambio de tab: ${this.tabs[event.index].type}`
        );

        // Guardar estado anterior
        const previousTab = this.currentTabType;

        // Actualizar estado del tab
        this.selectedTabIndex = event.index;
        this.currentTabType = this.tabs[event.index].type;

        // Reiniciar paginación
        this.paginaActual = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
            this.paginator.pageSize = 10; // Volver al tamaño por defecto
        }

        this.logger.info(
            `📋 Cambio de tab completado: ${JSON.stringify({
                from: previousTab,
                to: this.currentTabType,
                pageReset: {
                    page: this.paginaActual,
                    pageSize: this.elementosPorPagina,
                },
            })}`
        );

        // Buscar órdenes con la nueva configuración
        this.buscarOrdenes();
    }

    /**
     * Construye objeto de filtros basado en los controles del formulario
     */
    private buildFilters(): IOsAgenciaFilters {
        const filters: IOsAgenciaFilters = {};

        // Aplicar filtros según wireframe
        if (this.filtroServicio.value) {
            const serviceId = parseInt(String(this.filtroServicio.value));
            if (!isNaN(serviceId)) {
                filters.serviceId = serviceId;
            }
        }

        if (this.filtroNave.value) {
            const vesselId = parseInt(String(this.filtroNave.value));
            if (!isNaN(vesselId)) {
                filters.vesselId = vesselId;
            }
        }

        if (this.filtroTipoFacturacion.value) {
            filters.typeOfInvoicingId = this.filtroTipoFacturacion.value;
        }

        if (
            this.filtroCliente.value !== null &&
            this.filtroCliente.value !== undefined &&
            this.filtroCliente.value !== ''
        ) {
            const raw = this.filtroCliente.value as unknown;
            const clienteId =
                typeof raw === 'number' ? raw : parseInt(String(raw));
            if (!isNaN(clienteId)) filters.customerId = clienteId;
        }

        if (this.filtroContenedor.value?.trim()) {
            filters.containerNumber = this.filtroContenedor.value.trim();
        }

        if (this.filtroNroLiquidacion.value?.trim()) {
            const liquidacionId = parseInt(
                this.filtroNroLiquidacion.value.trim()
            );
            if (!isNaN(liquidacionId)) {
                filters.shippingLiquidationId = liquidacionId;
            }
        }

        if (this.filtroBooking.value?.trim()) {
            filters.bookingNumber = this.filtroBooking.value.trim();
        }

        if (this.filtroDus.value?.trim()) {
            filters.dusNumber = this.filtroDus.value.trim();
        }

        if (this.filtroTemporada.value) {
            filters.temporadaId = this.filtroTemporada.value;
        }

        if (this.filtroInicio.value && !this.filtroInicio.disabled) {
            filters.fromDate = this.formatDate(this.filtroInicio.value);
        }

        if (this.filtroFin.value && !this.filtroFin.disabled) {
            filters.toDate = this.formatDate(this.filtroFin.value);
        }

        return filters;
    }

    /**
     * Actualiza la lista filtrada de servicios para el autocompletar
     */
    private updateFilteredServicios(input: unknown): void {
        let term = '';
        if (typeof input === 'string') term = input.toLowerCase().trim();
        if (typeof input === 'number') {
            const match = this.serviciosOptions.find(
                (s) => s.serviceId === input
            );
            this.filteredServiciosOptions = match
                ? [match]
                : this.serviciosOptions.slice(0, AUTOCOMPLETE_MAX_OPTIONS);
            return;
        }
        if (term === '') {
            this.filteredServiciosOptions = this.serviciosOptions.slice(
                0,
                AUTOCOMPLETE_MAX_OPTIONS
            );
            return;
        }
        this.filteredServiciosOptions = this.serviciosOptions
            .filter(
                (s) =>
                    s.serviceName?.toLowerCase().includes(term) ||
                    String(s.serviceId).includes(term)
            )
            .slice(0, AUTOCOMPLETE_MAX_OPTIONS);
    }

    /**
     * Actualiza la lista filtrada de naves para el autocompletar
     */
    private updateFilteredNaves(input: unknown): void {
        let term = '';
        if (typeof input === 'string') term = input.toLowerCase().trim();
        if (typeof input === 'number') {
            const match = this.navesOptions.find((n) => n.vesselId === input);
            this.filteredNavesOptions = match
                ? [match]
                : this.navesOptions.slice(0, AUTOCOMPLETE_MAX_OPTIONS);
            return;
        }
        if (term === '') {
            this.filteredNavesOptions = this.navesOptions.slice(
                0,
                AUTOCOMPLETE_MAX_OPTIONS
            );
            return;
        }
        this.filteredNavesOptions = this.navesOptions
            .filter(
                (n) =>
                    n.vesselName?.toLowerCase().includes(term) ||
                    String(n.vesselId).includes(term)
            )
            .slice(0, AUTOCOMPLETE_MAX_OPTIONS);
    }

    /**
     * Mostrar nombre del servicio en el input del autocompletar
     */
    displayServicio = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.serviciosOptions.find(
                (s) => s.serviceId === value
            );
            return found ? found.serviceName : String(value);
        }
        return '';
    };

    /**
     * Mostrar nombre de la nave en el input del autocompletar
     */
    displayNave = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.navesOptions.find((n) => n.vesselId === value);
            return found ? found.vesselName : String(value);
        }
        return '';
    };

    /**
     * Actualiza la lista filtrada de clientes para el autocompletar
     */
    private updateFilteredClientes(input: unknown): void {
        let term = '';
        if (typeof input === 'string') term = input.toLowerCase().trim();
        if (typeof input === 'number') {
            const match = this.clientesOptions.find(
                (c) => c.clienteId === input
            );
            this.filteredClientesOptions = match
                ? [match]
                : this.clientesOptions.slice(0, AUTOCOMPLETE_MAX_OPTIONS);
            return;
        }
        if (term === '') {
            this.filteredClientesOptions = this.clientesOptions.slice(
                0,
                AUTOCOMPLETE_MAX_OPTIONS
            );
            return;
        }
        this.filteredClientesOptions = this.clientesOptions
            .filter(
                (c) =>
                    c.cliente?.toLowerCase().includes(term) ||
                    String(c.clienteId).includes(term)
            )
            .slice(0, AUTOCOMPLETE_MAX_OPTIONS);
    }

    /**
     * Mostrar nombre del cliente en el input del autocompletar
     */
    displayCliente = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.clientesOptions.find(
                (c) => c.clienteId === value
            );
            return found ? found.cliente : String(value);
        }
        return '';
    };

    /**
     * Aplica filtros y resetea el paginador a la primera página
     */
    aplicarFiltros(): void {
        // Resetear a la primera página cuando se aplican filtros
        this.paginaActual = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.buscarOrdenes();
    }

    /**
     * Busca órdenes según filtros y tab actual
     */
    async buscarOrdenes(): Promise<void> {
        this.isLoading = true;
        this.hasError = false;

        try {
            const filters = this.buildFilters();
            const orderBy = this.sort?.active || 'creationDate';
            const orderDirection =
                (this.sort?.direction.toUpperCase() as 'ASC' | 'DESC') ||
                'DESC';

            // Asegurarse de que los valores de paginación sean válidos
            const page = Math.max(1, this.paginaActual);
            const limit = Math.max(5, this.elementosPorPagina);

            const query: IOsAgenciaQuery = {
                ...filters,
                tabType: this.currentTabType,
                page,
                limit,
                orderBy,
                orderDirection,
            };

            this.logger.info(
                '📋 Query de paginación: ' +
                    JSON.stringify({
                        page: query.page,
                        limit: query.limit,
                        total: this.totalRegistros,
                        tabType: query.tabType,
                    })
            );

            this.logger.info(
                `📋 Buscando órdenes con filtros:`,
                JSON.stringify(query)
            );

            const response = await this.osAgenciaService
                .getOrdenes(query)
                .toPromise();

            if (response) {
                // Verificar si la respuesta es válida
                if (response.page !== page || response.limit !== limit) {
                    this.logger.warn(
                        '⚠️ Discrepancia en paginación: ' +
                            JSON.stringify({
                                requested: { page, limit },
                                received: {
                                    page: response.page,
                                    limit: response.limit,
                                },
                            })
                    );
                }

                this.processSearchResults(response);

                // Actualizar contadores de todos los tabs con los filtros aplicados
                await this.loadTabCounts();

                // Sincronizar paginador con el total del tab actual (por si el caché
                // devolvió un total diferente al que retorna la query filtrada por temporada)
                const currentTabTotal = this.tabCounts[this.currentTabType];
                if (
                    this.paginator &&
                    currentTabTotal > 0 &&
                    this.paginator.length !== currentTabTotal
                ) {
                    this.paginator.length = currentTabTotal;
                    this.totalRegistros = currentTabTotal;
                    this.totalPaginas = Math.ceil(
                        currentTabTotal / this.elementosPorPagina
                    );
                }
            }
        } catch (error) {
            this.handleError('Error al buscar órdenes de servicio', error);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Procesa los resultados de búsqueda
     */
    private processSearchResults(response: IOsAgenciaPaginatedResponse): void {
        try {
            // Mapear datos con campos calculados
            const mappedData: OrdenServicioAgenciaLocal[] = response.data.map(
                (item) => ({
                    ...item,
                    numeroLiquidacion: item.shippingLiquidationId
                        ? String(item.shippingLiquidationId).replace(/^0+/, '')
                        : 'N/A',
                    totalNeto:
                        item.totalSellingPriceAmount +
                        item.totalManagementFeeAmount,
                    formattedEtd: this.formatDisplayDate(item.etdDate),
                    formattedCreationDate: this.formatDisplayDate(
                        item.creationDate
                    ),
                })
            );

            // Actualizar datos y totales
            this.dataSource.data = mappedData;
            this.totalRegistros = response.total;
            this.totalPaginas = response.totalPages;

            // Actualizar variables locales primero
            this.totalRegistros = response.total;
            this.elementosPorPagina = response.limit;
            this.paginaActual = response.page;

            // Asegurarse de que el paginador esté sincronizado
            if (this.paginator) {
                this.logger.info(
                    '📊 Estado actual del paginador: ' +
                        JSON.stringify({
                            total: response.total,
                            pageSize: response.limit,
                            currentPage: response.page,
                            pageIndex: response.page - 1,
                            currentTabType: this.currentTabType,
                        })
                );

                // Actualizar el paginador solo si los valores son diferentes
                if (this.paginator.length !== response.total) {
                    this.paginator.length = response.total;
                }

                if (this.paginator.pageSize !== response.limit) {
                    this.paginator.pageSize = response.limit;
                }

                if (this.paginator.pageIndex !== response.page - 1) {
                    this.paginator.pageIndex = response.page - 1;
                }
            }

            // Asignar totales globales del backend (sobre TODOS los filtrados)
            this.assignGlobalTotals(response);

            // Actualizar contador del tab actual
            this.tabCounts[this.currentTabType] = response.total;

            this.logger.info(
                `✅ Procesados ${mappedData.length} registros de ${response.total} totales. ` +
                    `Página ${response.page} de ${response.totalPages}`
            );
        } catch (error) {
            this.logger.error(
                '❌ Error procesando resultados:',
                error as Error
            );
            throw error;
        }
    }

    /**
     * Asigna los totales globales recibidos del backend (sobre TODOS los registros filtrados)
     */
    private assignGlobalTotals(response: IOsAgenciaPaginatedResponse): void {
        if (response.totals) {
            this.totales = {
                count: response.total,
                totalVentaNeto: response.totals.totalVentaNeto || 0,
                totalTraspasoBruto: response.totals.totalTraspasoBruto || 0,
                totalGeneral: response.totals.totalGeneral || 0,
            };
        } else {
            this.totales = {
                count: response.total,
                totalVentaNeto: 0,
                totalTraspasoBruto: 0,
                totalGeneral: 0,
            };
        }
    }

    /**
     * Formatea fecha para mostrar en la UI
     */
    private formatDisplayDate(date: Date | string | null): string {
        if (!date) return 'N/A';

        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }

    /**
     * Formatea montos para mostrar en la tabla
     * Formato: $ X.XXX.XXX (pesos chilenos con espacio entre $ y el número)
     */
    formatMonto(value: number | null | undefined): string {
        if (value === null || value === undefined || isNaN(value)) {
            return '$ 0';
        }

        const formatted = new Intl.NumberFormat('es-CL', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);

        return `$ ${formatted}`;
    }

    /**
     * Obtiene el badge count para un tab
     */
    getTabBadgeCount(tabType: OsAgenciaTabType): number {
        return this.tabCounts[tabType] || 0;
    }

    /**
     * Verifica si hay filtros activos
     */
    hasActiveFilters(): boolean {
        return !!(
            this.filtroCliente.value ||
            this.filtroServicio.value ||
            this.filtroNave.value ||
            this.filtroTipoFacturacion.value ||
            this.filtroTemporada.value ||
            this.filtroInicio.value ||
            this.filtroFin.value ||
            this.filtroRefCliente.value ||
            this.filtroContenedor.value ||
            this.filtroDus.value ||
            this.filtroNroLiquidacion.value ||
            this.filtroBooking.value
        );
    }

    /**
     * Obtiene el nombre de la temporada por su ID para mostrar en el chip
     */
    getTemporadaNombre(temporadaId: number | null): string {
        if (!temporadaId) return '';
        const temporada = this.temporadasOptions.find(
            (t) => t.seasonId === temporadaId
        );
        return temporada?.seasonName || String(temporadaId);
    }

    /**
     * Limpia todos los filtros
     */
    limpiarFiltros(): void {
        const currentUser = this.authService.getCurrentUser();
        const isClientUser = currentUser?.roles.includes('client');

        // Si no es usuario cliente, limpiar el filtro de cliente
        if (!isClientUser) {
            this.filtroCliente.setValue('');
        }

        // Limpiar el resto de los filtros
        this.filtroServicio.setValue(null);
        this.filtroNave.setValue('');
        this.filtroTipoFacturacion.setValue(null);
        // Restaurar temporada a la más reciente (por defecto)
        if (this.temporadasOptions.length > 0) {
            this.filtroTemporada.setValue(this.temporadasOptions[0].seasonId);
        } else {
            this.filtroTemporada.setValue(null);
        }
        this.filtroInicio.setValue(null);
        this.filtroFin.setValue(null);
        this.filtroRefCliente.setValue('');
        this.filtroContenedor.setValue('');
        this.filtroDus.setValue('');
        this.filtroNroLiquidacion.setValue('');
        this.filtroBooking.setValue('');

        this.logger.info(
            '🧹 Filtros limpiados' +
                (isClientUser ? ' (manteniendo filtro de cliente)' : '')
        );

        // Reiniciar paginación
        this.paginaActual = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
            this.paginator.pageSize = 10; // Volver al tamaño por defecto
        }

        this.buscarOrdenes();
    }

    /**
     * Acciones sobre órdenes - VER
     * Abre el detalle en una nueva pestaña preservando filtros y paginación en la URL.
     */
    verOrden(orden: OrdenServicioAgenciaLocal, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }

        this.logger.info(
            `🔍 Abriendo detalle de orden de servicio en nueva pestaña: ${orden.id}`,
            'OrdenesServicioAgenciaComponent'
        );

        // Construir query params con filtros y paginación actuales
        const queryParams: any = {
            page: this.paginaActual,
            limit: this.elementosPorPagina,
        };

        // Agregar filtros activos
        const filters = this.buildFilters();
        if (filters.customerId) queryParams.customerId = filters.customerId;
        if (filters.serviceId) queryParams.serviceId = filters.serviceId;
        if (filters.vesselId) queryParams.vesselId = filters.vesselId;
        if (filters.typeOfInvoicingId)
            queryParams.typeOfInvoicingId = filters.typeOfInvoicingId;
        if (filters.containerNumber)
            queryParams.containerNumber = filters.containerNumber;
        if (filters.bookingNumber)
            queryParams.bookingNumber = filters.bookingNumber;
        if (filters.shippingLiquidationId)
            queryParams.shippingLiquidationId = filters.shippingLiquidationId;
        if (filters.fromDate) queryParams.fromDate = filters.fromDate;
        if (filters.toDate) queryParams.toDate = filters.toDate;

        // Agregar refCliente si existe
        if (this.filtroRefCliente.value?.trim()) {
            queryParams.refCliente = this.filtroRefCliente.value.trim();
        }

        const urlTree = this.router.createUrlTree(
            ['/ordenes-servicio-agencia', orden.id],
            { queryParams }
        );
        const url = this.router.serializeUrl(urlTree);
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    /**
     * Acciones sobre órdenes - DESCARGAR DOC
     */
    descargarDocumento(orden: OrdenServicioAgenciaLocal): void {
        this.logger.info(
            `📄 Abrir diálogo de documentos para orden ID: ${orden.id}`
        );

        const dialogRef = this.dialog.open(
            DocumentosOrdenServicioDialogComponent,
            {
                data: {
                    ordenServicioId: orden.id,
                    numeroOrdenServicio: orden.id.toString().padStart(8, '0'),
                },
                width: '90%',
                maxWidth: '1200px',
                panelClass: 'documentos-dialog',
            }
        );

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.logger.info(
                    'Diálogo de documentos cerrado con resultado:',
                    result
                );
            }
        });
    }

    /**
     * Exportar a Excel con las 3 hojas (tabs) sin paginación
     */
    async exportarExcel(): Promise<void> {
        this.logger.info(
            '📊 Exportando a Excel - Órdenes de Servicio (3 hojas)'
        );

        // Activar overlay de carga
        this.isGeneratingExcel = true;

        try {
            const filters = this.buildFilters();
            const orderBy = this.sort?.active || 'creationDate';
            const orderDirection =
                (this.sort?.direction.toUpperCase() as 'ASC' | 'DESC') ||
                'DESC';

            // Obtener datos de los 3 tabs sin paginación
            const [tab1Data, tab2Data, tab3Data] = await Promise.all([
                this.osAgenciaService
                    .getExcelData({
                        ...filters,
                        tabType: OsAgenciaTabType.ORDER_SERVICES,
                    })
                    .toPromise(),
                this.osAgenciaService
                    .getExcelData({
                        ...filters,
                        tabType: OsAgenciaTabType.TRANSPORT,
                    })
                    .toPromise(),
                this.osAgenciaService
                    .getExcelData({
                        ...filters,
                        tabType: OsAgenciaTabType.ADDITIONAL_COST,
                    })
                    .toPromise(),
            ]);

            // Crear Excel con 3 hojas
            await this.crearYDescargarExcel(
                {
                    ordenesServicio: tab1Data?.data || [],
                    transporte: tab2Data?.data || [],
                    extraCostos: tab3Data?.data || [],
                },
                `Ordenes_Servicio_Agencia_${
                    new Date().toISOString().split('T')[0]
                }.xlsx`
            );
        } catch (error) {
            this.logger.error('Error al exportar Excel', error as Error);
            this.snackBar.open('Error al exportar a Excel', 'Cerrar', {
                duration: 5000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            // Desactivar overlay de carga
            this.isGeneratingExcel = false;
        }
    }

    /**
     * Crea y descarga el archivo Excel con estilo OPB
     */
    private async crearYDescargarExcel(
        data: {
            ordenesServicio: any[];
            transporte: any[];
            extraCostos: any[];
        },
        nombreArchivo: string
    ): Promise<void> {
        try {
            const workbook = new ExcelJS.Workbook();

            // Colores de marca OPB
            const colors = {
                primary: '1e3a8a', // Azul primario
                accent: 'ef4444', // Rojo acento
                secondary: '3b82f6', // Azul secundario
                dark: '1e293b', // Azul oscuro
                gray: '64748b', // Gris
                light: 'f1f5f9', // Gris claro
                white: 'ffffff', // Blanco
            };

            // Cargar logo una vez para todas las hojas
            let logoArrayBuffer: ArrayBuffer | null = null;
            try {
                const baseHref =
                    document.querySelector('base')?.getAttribute('href') || '/';
                const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
                const logoResponse = await fetch(logoPath);
                if (logoResponse.ok) {
                    logoArrayBuffer = await logoResponse.arrayBuffer();
                } else {
                    const altResponse = await fetch(
                        'assets/images/logos/logotipo-fondo-claro.png'
                    );
                    if (altResponse.ok) {
                        logoArrayBuffer = await altResponse.arrayBuffer();
                    }
                }
            } catch (error) {
                this.logger.warn(
                    'No se pudo cargar el logo',
                    'OrdenesServicioAgenciaComponent'
                );
            }

            // Función auxiliar para aplicar estilos a una hoja
            const aplicarEstilosAhoja = async (
                worksheet: ExcelJS.Worksheet,
                titulo: string,
                rows: any[],
                columnHeaders: Array<{
                    header: string;
                    key: string;
                    width: number;
                }>
            ) => {
                // Agregar logo
                if (logoArrayBuffer) {
                    try {
                        const logoId = workbook.addImage({
                            buffer: logoArrayBuffer,
                            extension: 'png',
                        });
                        worksheet.addImage(logoId, {
                            tl: { col: 0.2, row: 0.2 },
                            ext: { width: 150, height: 50 },
                        });
                    } catch (error) {
                        this.logger.warn(
                            'Error al agregar logo',
                            'OrdenesServicioAgenciaComponent'
                        );
                    }
                }

                // Título del reporte (fila 1, columna D)
                const titleRow = worksheet.getRow(1);
                titleRow.height = 40;
                const titleCell = worksheet.getCell('D1');
                titleCell.value = titulo.toUpperCase();
                titleCell.font = {
                    name: 'Calibri',
                    size: 20,
                    bold: true,
                    color: { argb: 'FF' + colors.primary },
                };
                titleCell.alignment = {
                    vertical: 'middle',
                    horizontal: 'left',
                };
                const lastCol = String.fromCharCode(64 + columnHeaders.length);
                worksheet.mergeCells(`D1:${lastCol}1`);

                // Fecha de generación (fila 2, columna D)
                const dateRow = worksheet.getRow(2);
                dateRow.height = 25;
                const dateCell = worksheet.getCell('D2');
                dateCell.value = `Generado el: ${new Date().toLocaleDateString(
                    'es-CL',
                    {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }
                )}`;
                dateCell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF' + colors.gray },
                };
                dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
                worksheet.mergeCells(`D2:${lastCol}2`);

                // Fila vacía
                worksheet.getRow(3).height = 10;

                // Configurar anchos de columna
                columnHeaders.forEach((col, index) => {
                    worksheet.getColumn(index + 1).width = col.width;
                });

                // Crear headers manualmente (fila 4)
                const headerRow = worksheet.getRow(4);
                headerRow.height = 30;

                columnHeaders.forEach((col, index) => {
                    const cell = worksheet.getCell(4, index + 1);
                    cell.value = col.header;
                    cell.font = {
                        name: 'Calibri',
                        size: 12,
                        bold: true,
                        color: { argb: 'FFFFFFFF' }, // Blanco
                    };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF' + colors.primary },
                    };
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                        wrapText: true,
                    };
                    cell.border = {
                        top: {
                            style: 'medium',
                            color: { argb: 'FF' + colors.dark },
                        },
                        bottom: {
                            style: 'medium',
                            color: { argb: 'FF' + colors.dark },
                        },
                        left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                        right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                    };
                });

                // Función auxiliar para formatear fechas en DD-MM-YYYY
                const formatDateForExcel = (value: any): string => {
                    if (!value) return '';
                    if (typeof value === 'string') {
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(
                                2,
                                '0'
                            );
                            const year = date.getFullYear();
                            return `${day}-${month}-${year}`;
                        }
                    }
                    if (value instanceof Date) {
                        const day = String(value.getDate()).padStart(2, '0');
                        const month = String(value.getMonth() + 1).padStart(
                            2,
                            '0'
                        );
                        const year = value.getFullYear();
                        return `${day}-${month}-${year}`;
                    }
                    return String(value);
                };

                // Campos que son fechas (en camelCase)
                const dateFields = ['etdDate', 'liquidationDate'];

                // Agregar datos
                rows.forEach((item, index) => {
                    const rowNumber = 5 + index;
                    const row = worksheet.getRow(rowNumber);

                    columnHeaders.forEach((col, colIndex) => {
                        const cell = row.getCell(colIndex + 1);
                        const rawValue = item[col.key];
                        let cellValue: any =
                            rawValue !== undefined && rawValue !== null
                                ? rawValue
                                : '';

                        // Formatear fechas en formato DD-MM-YYYY
                        if (dateFields.includes(col.key) && rawValue) {
                            cellValue = formatDateForExcel(rawValue);
                        }

                        cell.value = cellValue;

                        // Fondo alternado
                        const fillColor =
                            index % 2 === 0
                                ? { argb: 'FFFFFFFF' } // Blanco
                                : { argb: 'FF' + colors.light }; // Gris claro
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: fillColor,
                        };
                        cell.font = {
                            name: 'Calibri',
                            size: 11,
                            color: { argb: 'FF000000' }, // Negro
                        };

                        // Alineación según tipo de dato
                        if (typeof cellValue === 'number') {
                            cell.alignment = {
                                vertical: 'middle',
                                horizontal: 'right',
                            };
                            cell.numFmt = '#,##0';
                        } else {
                            cell.alignment = {
                                vertical: 'middle',
                                horizontal: 'left',
                            };
                        }

                        // Bordes
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                            bottom: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                            left: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                            right: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                        };
                    });

                    row.height = 22;
                });
            };

            // Hoja 1: Órdenes de Servicio
            if (data.ordenesServicio.length > 0) {
                const ws = workbook.addWorksheet('Órdenes de Servicio');
                const firstRow = data.ordenesServicio[0];
                const headers = Object.keys(firstRow).map((key) => ({
                    header: this.capitalizeHeader(key),
                    key: key,
                    width: this.getColumnWidth(key),
                }));
                await aplicarEstilosAhoja(
                    ws,
                    'Órdenes de Servicio Agencia',
                    data.ordenesServicio,
                    headers
                );
            }

            // Hoja 2: Transporte
            if (data.transporte.length > 0) {
                const ws = workbook.addWorksheet('Transporte');
                const firstRow = data.transporte[0];
                const headers = Object.keys(firstRow).map((key) => ({
                    header: this.capitalizeHeader(key),
                    key: key,
                    width: this.getColumnWidth(key),
                }));
                await aplicarEstilosAhoja(
                    ws,
                    'Órdenes de Servicio Transporte',
                    data.transporte,
                    headers
                );
            }

            // Hoja 3: Extra Costos
            if (data.extraCostos.length > 0) {
                const ws = workbook.addWorksheet('Extra Costos');
                const firstRow = data.extraCostos[0];
                const headers = Object.keys(firstRow).map((key) => ({
                    header: this.capitalizeHeader(key),
                    key: key,
                    width: this.getColumnWidth(key),
                }));
                await aplicarEstilosAhoja(
                    ws,
                    'Órdenes de Servicio Extra Costos',
                    data.extraCostos,
                    headers
                );
            }

            if (workbook.worksheets.length === 0) {
                this.snackBar.open(
                    'No hay datos para generar el archivo Excel.',
                    'Cerrar',
                    {
                        duration: 3000,
                        panelClass: ['warning-snackbar'],
                    }
                );
                return;
            }

            // Generar y descargar archivo
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = nombreArchivo;
            link.click();
            window.URL.revokeObjectURL(url);

            this.snackBar.open('Excel generado exitosamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error(
                'Error al crear el archivo Excel',
                error instanceof Error ? error : new Error(String(error)),
                'OrdenesServicioAgenciaComponent'
            );
            this.snackBar.open(
                'Error al procesar los datos para el Excel',
                'Cerrar',
                {
                    duration: 5000,
                    panelClass: ['error-snackbar'],
                }
            );
        }
    }

    /**
     * Capitaliza y traduce los nombres de columnas para Excel
     */
    private capitalizeHeader(key: string): string {
        const headerMap: { [key: string]: string } = {
            osNumber: 'N° O/S',
            shippingInstructionId: 'N° I/E',
            transportOrderId: 'N° O/T',
            customerName: 'Cliente',
            customerReference: 'Ref. Cliente',
            voyageNumber: 'Viaje',
            portOfLoading: 'POL',
            terminal: 'Terminal',
            etdDate: 'ETD',
            portOfDischarge: 'POD',
            countryOfDischarge: 'País Destino',
            typeOfShipment: 'Tipo Embarque',
            shipConvenience: 'Ship Convenience',
            serviceName: 'Servicio',
            typeOfInvoicing: 'Tipo Facturación',
            netSale: 'Venta Neta',
            grossTransfer: 'Traspaso Bruto',
            providerName: 'Proveedor',
            providerRut: 'RUT Proveedor',
            providerInvoice: 'N° Factura',
            containers: 'Contenedores',
            customerComments: 'Comentarios',
            liquidationDate: 'Fecha Liquidación',
            liquidationId: 'N° Liquidación',
        };
        return headerMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
    }

    /**
     * Obtiene el ancho recomendado para una columna
     */
    private getColumnWidth(key: string): number {
        const widthMap: { [key: string]: number } = {
            osNumber: 12,
            shippingInstructionId: 12,
            transportOrderId: 12,
            customerName: 25,
            customerReference: 15,
            voyageNumber: 20,
            portOfLoading: 20,
            terminal: 20,
            etdDate: 15,
            portOfDischarge: 20,
            countryOfDischarge: 20,
            typeOfShipment: 15,
            shipConvenience: 15,
            serviceName: 30,
            typeOfInvoicing: 15,
            netSale: 15,
            grossTransfer: 15,
            providerName: 30,
            providerRut: 15,
            providerInvoice: 15,
            containers: 12,
            customerComments: 50,
            liquidationDate: 18,
            liquidationId: 15,
        };
        return widthMap[key] || 15;
    }

    /**
     * Manejo de errores
     */
    private handleError(message: string, error: any): void {
        this.hasError = true;
        this.errorMessage = message;
        this.logger.error(message, error);
        this.snackBar.open(message, 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar'],
        });
    }

    /**
     * Formatea fecha para envío al backend (YYYY-MM-DD en hora local, evita desfase por timezone)
     */
    private formatDate(date: Date): string {
        return formatDateForApi(date);
    }

    formatDetailDate(date?: Date | string | null): string {
        if (!date) return 'N/D';
        const parsed = new Date(date);
        if (Number.isNaN(parsed.getTime())) {
            return 'N/D';
        }
        return parsed.toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }

    getComentariosCliente(text?: string | null): string {
        return text?.trim() || 'Sin comentarios';
    }
}
